import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Tab, Tabs } from "@mui/material";

import { MainLeftTitle } from "../../../_uiComponents/WrapperComponents";
import AddWinnerManualForm from "./views/AddWinnerManualForm";
import AddWinnersImport from "./views/AddWinnersImport";
import AddWinnersResults from "./views/AddWinnersResults";

import { retailSalesConstants } from "../../../../_redux/_constants/retailSales.constants";
import {
     retailSalesAddWinnersRequest,
     retailSalesClearAddWinnersResult,
     retailSalesDealerLookupRequest,
     retailSalesGetWinnerFilterOptionsRequest,
} from "../../../../_redux/_actions";

export default function AddWinnersWrapper() {
     const actionDispatch = useDispatch();
     const navigate = useNavigate();

     const filterOptions = useSelector(
          (state) => state?.retailSales?.winnerFilterOptions,
     );
     // Every dealer, not only the ones already flagged as retail. A dealer earns the flag by
     // producing a winner, so restricting the picker to flagged dealers would mean no dealer could
     // ever be the first winner's dealer.
     const dealerLookupList = useSelector(
          (state) => state?.retailSales?.dealerLookupList,
     );
     const addWinnersResult = useSelector(
          (state) => state?.retailSales?.addWinnersResult,
     );
     // True from the moment the batch is sent until the result or an error comes back, so the add
     // button cannot be pressed a second time and pay every winner twice.
     const isSubmitting = useSelector((state) =>
          (state?.loading?.pendingRequests || []).includes(
               retailSalesConstants.RETAIL_SALES_ADD_WINNERS_REQUEST,
          ),
     );

     const [selectedTab, setSelectedTab] = useState(0);
     const [dealerSearch, setDealerSearch] = useState("");
     const [submittedRows, setSubmittedRows] = useState([]);

     // The roles and job titles are fixed lists, so they are read once.
     useEffect(() => {
          actionDispatch(retailSalesGetWinnerFilterOptionsRequest({}));
     }, [actionDispatch]);

     // The dealer picker is searchable and the backend matches what was typed.
     useEffect(() => {
          const timer = setTimeout(() => {
               actionDispatch(
                    retailSalesDealerLookupRequest({
                         search: dealerSearch || undefined,
                    }),
               );
          }, 300);

          return () => clearTimeout(timer);
     }, [actionDispatch, dealerSearch]);

     useEffect(() => {
          return () => {
               actionDispatch(retailSalesClearAddWinnersResult());
          };
     }, [actionDispatch]);

     const dealers = dealerLookupList || [];
     const jobRoles = filterOptions?.jobRoles || [];
     const jobTitles = filterOptions?.jobTitles || [];

     const dealerOptions = useMemo(
          () =>
               dealers.map((dealer) => ({
                    id: dealer.id,
                    dealerCode: dealer.dealerCode,
                    name: dealer.name,
                    label: `${dealer.dealerCode} - ${dealer.name}`,
               })),
          [dealers],
     );

     const submitWinners = useCallback(
          (winners) => {
               setSubmittedRows(winners);
               actionDispatch(retailSalesAddWinnersRequest({ winners }));
          },
          [actionDispatch],
     );

     const done = useCallback(() => {
          actionDispatch(retailSalesClearAddWinnersResult());
          navigate("/app/retail-sales-winners");
     }, [actionDispatch, navigate]);

     return (
          <>
               <MainLeftTitle title="Add Retail Sales Winners" />

               <div className="sba-telerik-components-wr">
                    {/* Either the entry form or the outcome is on screen, never both. */}
                    {addWinnersResult ? (
                         <AddWinnersResults
                              result={addWinnersResult}
                              rows={submittedRows}
                              onDone={done}
                         />
                    ) : (
                    <>
                    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                         <Tabs
                              value={selectedTab}
                              onChange={(event, value) => {
                                   // The import resolves dealer codes against the full list, so a
                                   // search typed on the manual tab is cleared when leaving it.
                                   setDealerSearch("");
                                   setSelectedTab(value);
                              }}
                         >
                              <Tab label="Add one winner" />
                              <Tab label="Import weekly list" />
                         </Tabs>
                    </Box>

                    <Box sx={{ pt: 3 }}>
                         {selectedTab === 0 && (
                              <AddWinnerManualForm
                                   dealerOptions={dealerOptions}
                                   jobRoleOptions={jobRoles}
                                   jobTitleOptions={jobTitles}
                                   isSubmitting={isSubmitting}
                                   onDealerSearch={setDealerSearch}
                                   onSubmit={submitWinners}
                              />
                         )}
                         {selectedTab === 1 && (
                              <AddWinnersImport
                                   dealerOptions={dealerOptions}
                                   jobRoleOptions={jobRoles}
                                   jobTitleOptions={jobTitles}
                                   isSubmitting={isSubmitting}
                                   onSubmit={submitWinners}
                              />
                         )}
                    </Box>
                    </>
                    )}
               </div>
          </>
     );
}
